import type { Department } from "../../../contexts/AuthProvider";
import { getJobDisplayName } from "../../../utils/roles";
import type { PrintoutColumnType, ScheduleRow } from "../types";
import { resolveRoleShortCode } from "./roleHelpers";
import { formatShiftTimeRange } from "./shiftTimeFormat";

interface GetRowDisplayValueArgs {
	row: ScheduleRow;
	colType: PrintoutColumnType;
	timezone: string;
	jobManagementEnabled: boolean;
	department?: Department;
}

/**
 * Resolve a row's **display value** for a column kind.
 *
 * This is the **renderable string** the user sees — distinct from the sort key (mapping §5.5
 * display/sort-key decoupling) and the group key. Used by:
 * - Horizontal pivot layout to build the axis band labels (mapping §8.2)
 * - Cell renderers for non-decorated cell values
 *
 * Empty-value fallback for `floor` / `location` uses the same sentinel labels as section grouping
 * (`"No Floor"`, `"No Location"`) so the pivot axis labels match what users see in section headers.
 */
export const getRowDisplayValue = ({ row, colType, timezone, jobManagementEnabled, department }: GetRowDisplayValueArgs): string => {
	switch (colType) {
		case "certification":
			return resolveRoleShortCode(row.nurse?.role, row.role);
		case "role":
			if (jobManagementEnabled && department) {
				return row.nurse?.jobId ? getJobDisplayName(row.nurse.jobId, department) : "";
			}
			return resolveRoleShortCode(row.nurse?.role, row.role);
		case "shiftTime":
			return formatShiftTimeRange(row.shiftStart, row.shiftEnd, timezone);
		case "floor":
			return row.floor || "No Floor";
		case "location":
			return row.location || "No Location";
		case "name":
			return row.nurse?.name ?? "";
		default:
			return "";
	}
};
